/**
 * Speaker normalisation.
 *
 * The digest pass recorded speaker names free-form, so the same person appears
 * under a dozen spellings ("Naamdeo" / "Naam Deo", "Vigyandeo" / "Vigyan Deo" /
 * "Vigyandev", ...). This maps every spelling — Latin or Devanagari — onto one
 * canonical display title and a stable URL slug, using full honorifics.
 */
import type { Speaker } from "./types";

const SWATANTRA_DEO: Speaker = {
  key: "swatantra-deo",
  name: "Sadguru Shri Swatantra Deo Ji Maharaj",
};
const VIGYAN_DEO: Speaker = {
  key: "vigyan-deo",
  name: "Sant Pravar Shri Vigyan Deo Ji Maharaj",
};
const NAAM_DEO: Speaker = {
  key: "naam-deo",
  name: "Sant Shri Naam Deo Ji Maharaj",
};
const SADAFAL_DEO: Speaker = {
  key: "sadafal-deo",
  name: "Anant Shri Sadguru Sadafal Deo Ji Maharaj",
};
const DHARMCHANDRA_DEO: Speaker = {
  key: "dharmchandra-deo",
  name: "Acharya Shri Dharmchandra Deo Ji Maharaj",
};
const UNKNOWN_SPEAKER: Speaker = {
  key: "unknown",
  name: "Unknown speaker",
};

interface SpeakerMatcher {
  readonly speaker: Speaker;
  readonly patterns: readonly RegExp[];
}

const MATCHERS: readonly SpeakerMatcher[] = [
  { speaker: SWATANTRA_DEO, patterns: [/swatantra/i, /स्वतंत्र/u] },
  {
    speaker: VIGYAN_DEO,
    // "Sant Pravar" is this speaker's own honorific in Vihangam Yog, so a title
    // that uses it without spelling his name still resolves to him.
    patterns: [
      /vigyan[\s-]?de[vo]/i,
      /विज्ञान\s?देव/u,
      /sant\s+pravar/i,
      /संत\s+प्रवर/u,
    ],
  },
  { speaker: NAAM_DEO, patterns: [/naam[\s-]?de[vo]/i, /नाम\s?देव/u] },
  { speaker: SADAFAL_DEO, patterns: [/sadafal/i, /सदाफल/u] },
  {
    speaker: DHARMCHANDRA_DEO,
    patterns: [/dharm\s?chandra/i, /dharamchandra/i, /धर्मचन्?द्र/u],
  },
];

/** The canonical speakers, in the order the Speakers page lists them. */
const SPEAKERS: readonly Speaker[] = [
  SWATANTRA_DEO,
  VIGYAN_DEO,
  NAAM_DEO,
  SADAFAL_DEO,
  DHARMCHANDRA_DEO,
  UNKNOWN_SPEAKER,
];

/**
 * The speaker mentioned earliest in `text`, or `null` if none. Earliest-wins so
 * a combined credit ("Naamdeo & Vigyandeo") resolves to the first named.
 */
function detect(text: string): Speaker | null {
  let best: Speaker | null = null;
  let bestIndex = Number.POSITIVE_INFINITY;
  for (const matcher of MATCHERS) {
    for (const pattern of matcher.patterns) {
      const index = text.search(pattern);
      if (index !== -1 && index < bestIndex) {
        bestIndex = index;
        best = matcher.speaker;
      }
    }
  }
  return best;
}

/** Normalise a raw digest speaker string to a canonical speaker. */
export function normalizeSpeaker(raw: string): Speaker {
  return detect(raw) ?? UNKNOWN_SPEAKER;
}

/**
 * Best-effort speaker for a video with no digest, read from its title. Limited
 * to the three living discourse-givers: a title that merely mentions the
 * founder (e.g. his death-anniversary programme) must not be attributed to him.
 */
const TITLE_INFERABLE = new Set<string>([
  SWATANTRA_DEO.key,
  VIGYAN_DEO.key,
  NAAM_DEO.key,
]);

export function inferSpeakerFromTitle(title: string): Speaker {
  const found = detect(title);
  if (found !== null && TITLE_INFERABLE.has(found.key)) {
    return found;
  }
  return UNKNOWN_SPEAKER;
}

/** Look up a canonical speaker by slug, falling back to the unknown speaker. */
export function speakerByKey(key: string): Speaker {
  return SPEAKERS.find((speaker) => speaker.key === key) ?? UNKNOWN_SPEAKER;
}
